import { useState, useEffect, useRef } from 'react';
import { Storage } from '../storage';

// ─── AUTH SCREEN ──────────────────────────────────────────────────────────────
//
//  Handles both Sign In and Sign Up within the same card using tab state.
//  All user data is persisted to localStorage via the Storage service.
//
//  Props:
//    onLogin {function}  called with the authenticated user object on success
//

// Password must be 8–128 chars with uppercase, lowercase, and a number
const validatePassword = (pw) => {
  if (pw.length < 8)         return 'Password must be at least 8 characters.';
  if (pw.length > 128)       return 'Password must be 128 characters or fewer.';
  if (!/[A-Z]/.test(pw))    return 'Password must include at least one uppercase letter.';
  if (!/[a-z]/.test(pw))    return 'Password must include at least one lowercase letter.';
  if (!/\d/.test(pw))       return 'Password must include at least one number.';
  return null;
};

const AuthScreen = ({ onLogin, onGuestEntry, expiredNotice = false }) => {

  const [mode,       setMode]       = useState('login'); // 'login' | 'signup' | 'forgot' | 'reset'
  const [name,       setName]       = useState('');
  const [email,      setEmail]      = useState('');
  const [password,   setPassword]   = useState('');
  const [confirm,    setConfirm]    = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error,      setError]      = useState('');
  const [notice,     setNotice]     = useState(''); // non-error info message
  const [loading,    setLoading]    = useState(false);
  const [agreedToTerms,   setAgreedToTerms]   = useState(false);
  const [pending,         setPending]         = useState(false);
  const [resending,       setResending]       = useState(false);
  const [rememberMe,      setRememberMe]      = useState(false);
  const [forgotSentAt,    setForgotSentAt]    = useState(null);  // timestamp of last successful send
  const [forgotCountdown, setForgotCountdown] = useState(0);     // seconds remaining in cooldown
  const [pwFocused,       setPwFocused]       = useState(false);
  const countdownRef = useRef(null);

  const pwRequirements = [
    { label: 'At least 8 characters',        met: password.length >= 8 },
    { label: 'At least one uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'At least one lowercase letter', met: /[a-z]/.test(password) },
    { label: 'At least one number',           met: /[0-9]/.test(password) },
  ];
  const pwAllMet = pwRequirements.every(r => r.met);

  // Check for ?verified=true, ?auth_error=…, or ?reset_token=… after redirects.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') === 'true') {
      setNotice('Email verified! You can now sign in.');
      setMode('login');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('auth_error')) {
      setError('That verification link is invalid or has already been used.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('reset_token')) {
      setResetToken(params.get('reset_token'));
      setMode('reset');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // 15-minute countdown shown after a successful forgot-password submission.
  useEffect(() => {
    if (!forgotSentAt) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((forgotSentAt + 15 * 60 * 1000 - Date.now()) / 1000));
      setForgotCountdown(remaining);
      if (remaining === 0) { clearInterval(countdownRef.current); setForgotSentAt(null); }
    };
    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => clearInterval(countdownRef.current);
  }, [forgotSentAt]);

  const switchMode = (m) => { setMode(m); setError(''); setNotice(''); setConfirm(''); setPassword(''); setPending(false); setAgreedToTerms(false); setPwFocused(false); };

  const handleSubmit = async () => {
    setError('');
    setNotice('');
    setLoading(true);

    try {
      if (mode === 'signup') {
        if (!name.trim() || !email.trim() || !password.trim()) {
          setError('All fields are required.'); setLoading(false); return;
        }
        const pwError = validatePassword(password);
        if (pwError) { setError(pwError); setLoading(false); return; }
        if (password !== confirm) {
          setError('Passwords do not match.'); setLoading(false); return;
        }
        if (!agreedToTerms) {
          setError('You must agree to the Terms of Service and Privacy Policy.'); setLoading(false); return;
        }
        await Storage.signup(name.trim(), email.trim(), password, agreedToTerms);
        setPending(true);
      } else if (mode === 'forgot') {
        if (!email.trim()) { setError('Email is required.'); setLoading(false); return; }
        // Client-side 60-second guard — UX only, real protection is server-side.
        if (forgotSentAt && Date.now() - forgotSentAt < 60_000) {
          setError('Please wait a moment before requesting another link.');
          setLoading(false); return;
        }
        await Storage.forgotPassword(email.trim());
        setForgotSentAt(Date.now());
        setNotice('If that email is registered, a reset link is on its way. Check your inbox.');
      } else if (mode === 'reset') {
        const pwError = validatePassword(password);
        if (pwError) { setError(pwError); setLoading(false); return; }
        if (password !== confirm) { setError('Passwords do not match.'); setLoading(false); return; }
        await Storage.resetPassword(resetToken, password);
        setNotice('Password updated! You can now sign in with your new password.');
        setMode('login');
        setPassword('');
        setConfirm('');
      } else {
        const user = await Storage.login(email.trim(), password, rememberMe);
        onLogin(user);
      }
    } catch (e) {
      if (e.message === 'suspended') {
        setError('Your account has been suspended. Contact support if you believe this is an error.');
      } else {
        setError(e.message || 'Something went wrong. Please try again.');
      }
    }

    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await Storage.resendVerification(email.trim());
      setNotice('Verification email resent — check your inbox.');
    } catch (e) {
      setError(e.message || 'Could not resend email. Please try again.');
    }
    setResending(false);
  };

  // Inline resend for the "verify your email" login-fail state.
  const handleResendInline = async () => {
    if (!email.trim()) return;
    setResending(true);
    try {
      await Storage.resendVerification(email.trim());
      setError('');
      setNotice('Verification email resent — check your inbox.');
    } catch (e) {
      setError(e.message || 'Could not resend email. Please try again.');
    }
    setResending(false);
  };

  const handleKey = (e) => { if (e.key === 'Enter') handleSubmit(); };

  // ── "Check your email" screen shown after successful signup ─────────────────
  if (pending) {
    return (
      <div
        className="auth-screen"
        style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 24 }}
      >
        <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }} aria-hidden="true">📧</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, color: 'var(--accent)', marginBottom: 12 }}>
            Check your email
          </h2>
          <p style={{ color: 'var(--text2)', fontSize: 15, lineHeight: 1.6, marginBottom: 8 }}>
            We sent a verification link to <strong style={{ color: 'var(--text)' }}>{email}</strong>.
          </p>
          <p style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 28 }}>
            Click the link in the email to activate your account, then come back here to sign in.
          </p>

          {notice && (
            <p style={{ color: 'var(--success)', fontSize: 13, marginBottom: 16 }} aria-live="polite">
              {notice}
            </p>
          )}
          {error && (
            <p role="alert" aria-live="assertive" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
              {error}
            </p>
          )}

          <button
            className="btn-ghost"
            onClick={handleResend}
            disabled={resending}
            style={{ marginBottom: 16, opacity: resending ? 0.7 : 1 }}
          >
            {resending ? 'Sending…' : 'Resend verification email'}
          </button>

          <div>
            <button
              className="btn-ghost btn-sm"
              onClick={() => switchMode('login')}
              style={{ color: 'var(--text3)', fontSize: 13 }}
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Forgot password screen ────────────────────────────────────────────────
  if (mode === 'forgot') {
    return (
      <div
        className="auth-screen"
        style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 24 }}
      >
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div className="auth-logo" style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }} aria-hidden="true">⏱</div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 36, color: 'var(--accent)', letterSpacing: '-0.02em' }}>Chronos</h1>
          </div>
          <div className="auth-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 32 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 8 }}>Reset your password</h2>
            <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 24, lineHeight: 1.5 }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>

            {notice && (
              <p style={{ color: 'var(--success)', fontSize: 13, marginBottom: 16 }} aria-live="polite">{notice}</p>
            )}
            {error && (
              <p role="alert" aria-live="assertive" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>{error}</p>
            )}

            {!notice && (
              <div style={{ marginBottom: 20 }}>
                {/* Off-screen honeypot — Chrome fills the autofilled password here (rendered but invisible) instead of into the email field */}
                <input
                  type="password"
                  tabIndex={-1}
                  aria-hidden="true"
                  autoComplete="current-password"
                  style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                />
                <label htmlFor="forgot-email">Email</label>
                <input
                  id="forgot-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  onKeyDown={handleKey}
                  autoComplete="username"
                />
              </div>
            )}

            {notice ? (
              forgotCountdown > 0 && (
                <p style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', marginTop: 4 }}>
                  You can request another link in {Math.floor(forgotCountdown / 60)}:{String(forgotCountdown % 60).padStart(2, '0')}
                </p>
              )
            ) : (
              <>
                <button
                  className="btn-primary"
                  onClick={handleSubmit}
                  disabled={loading || forgotCountdown > 0}
                  style={{ width: '100%', opacity: loading || forgotCountdown > 0 ? 0.7 : 1 }}
                >
                  {loading ? 'Sending…' : forgotCountdown > 0
                    ? `Wait ${Math.floor(forgotCountdown / 60)}:${String(forgotCountdown % 60).padStart(2, '0')}`
                    : 'Send Reset Link'}
                </button>
                {forgotCountdown > 0 && (
                  <p style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', marginTop: 8 }}>
                    You can request another link in {Math.floor(forgotCountdown / 60)}:{String(forgotCountdown % 60).padStart(2, '0')}
                  </p>
                )}
              </>
            )}

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button className="btn-ghost btn-sm" onClick={() => switchMode('login')} style={{ color: 'var(--text3)', fontSize: 13 }}>
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Reset password screen (arrived via ?reset_token=… link) ──────────────
  if (mode === 'reset') {
    return (
      <div
        className="auth-screen"
        style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 24 }}
      >
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div className="auth-logo" style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }} aria-hidden="true">⏱</div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 36, color: 'var(--accent)', letterSpacing: '-0.02em' }}>Chronos</h1>
          </div>
          <div className="auth-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 32 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 8 }}>Choose a new password</h2>
            <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 24, lineHeight: 1.5 }}>
              Pick a strong password for your Chronos account.
            </p>

            {error && (
              <p role="alert" aria-live="assertive" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>{error}</p>
            )}

            <div style={{ marginBottom: 16 }}>
              <label htmlFor="reset-password">New Password</label>
              <input
                id="reset-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                onKeyDown={handleKey}
                autoComplete="new-password"
                onFocus={() => setPwFocused(true)}
                onBlur={() => setPwFocused(false)}
              />
              {(pwFocused || password) && (
                <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, fontSize: 11, lineHeight: 1.7 }}>
                  {pwRequirements.map(r => (
                    <li key={r.label} style={{ color: r.met ? 'var(--success, #4caf50)' : 'var(--danger)' }}>
                      {r.met ? '✓' : '✗'} {r.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div style={{ marginBottom: 20 }}>
              <label htmlFor="reset-confirm">Confirm New Password</label>
              <input
                id="reset-confirm"
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="••••••••"
                onKeyDown={handleKey}
                autoComplete="new-password"
                style={confirm && confirm !== password ? { borderColor: 'var(--danger)' } : {}}
              />
              {confirm && confirm !== password && (
                <p style={{ fontSize: 11, color: 'var(--danger)', marginTop: 6 }}>Passwords do not match.</p>
              )}
            </div>

            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading || (password.length > 0 && !pwAllMet)}
              style={{ width: '100%', opacity: (loading || (password.length > 0 && !pwAllMet)) ? 0.7 : 1 }}
            >
              {loading ? 'Saving…' : 'Set New Password'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button className="btn-ghost btn-sm" onClick={() => switchMode('login')} style={{ color: 'var(--text3)', fontSize: 13 }}>
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Normal sign-in / sign-up card ─────────────────────────────────────────
  return (
    <div
      className="auth-screen"
      style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 24 }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo */}
        <div className="auth-logo" style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }} aria-hidden="true">⏱</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 36, color: 'var(--accent)', letterSpacing: '-0.02em' }}>
            Chronos
          </h1>
          <p style={{ color: 'var(--text2)', marginTop: 8, fontSize: 15 }}>
            Build beautiful interactive timelines
          </p>
        </div>

        {/* Card */}
        <div
          className="auth-card"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 32 }}
        >
          {/* Tab switcher */}
          <div
            role="tablist"
            aria-label="Authentication mode"
            style={{ display: 'flex', gap: 8, marginBottom: 28, background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: 4 }}
          >
            {[['login', 'Sign In'], ['signup', 'Sign Up']].map(([m, label]) => (
              <button
                key={m}
                role="tab"
                aria-selected={mode === m}
                onClick={() => switchMode(m)}
                style={{
                  flex: 1, padding: '8px', borderRadius: 8,
                  background: mode === m ? 'var(--accent)' : 'transparent',
                  color: mode === m ? '#1a1408' : 'var(--text2)',
                  fontWeight: 500, fontSize: 14, border: 'none',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Session expired notice */}
          {expiredNotice && mode === 'login' && (
            <p style={{ color: 'var(--accent)', fontSize: 13, marginBottom: 16, background: 'var(--accent)15', borderRadius: 8, padding: '10px 14px' }} aria-live="polite">
              Your session expired. Sign in again to continue.
            </p>
          )}

          {/* Success notice (e.g. after email verified redirect) */}
          {notice && (
            <p style={{ color: 'var(--success)', fontSize: 13, marginBottom: 16 }} aria-live="polite">
              {notice}
            </p>
          )}

          {/* Name field (signup only) */}
          {mode === 'signup' && (
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="auth-name">Full Name</label>
              <input
                id="auth-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Jane Smith"
                onKeyDown={handleKey}
                autoComplete="name"
              />
            </div>
          )}

          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              onKeyDown={handleKey}
              autoComplete="email"
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: mode === 'signup' ? 16 : 8 }}>
            <label htmlFor="auth-password">Password</label>
            <input
              id="auth-password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              onKeyDown={handleKey}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              onFocus={() => setPwFocused(true)}
              onBlur={() => setPwFocused(false)}
            />
            {mode === 'signup' && (pwFocused || password) && (
              <ul style={{ listStyle: 'none', margin: '6px 0 0', padding: 0, fontSize: 11, lineHeight: 1.7 }}>
                {pwRequirements.map(r => (
                  <li key={r.label} style={{ color: r.met ? 'var(--success, #4caf50)' : 'var(--danger)' }}>
                    {r.met ? '✓' : '✗'} {r.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Remember Me + Forgot password (login only) */}
          {mode === 'login' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <label htmlFor="auth-remember" style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, color: 'var(--text2)', margin: 0, userSelect: 'none' }}>
                <input
                  id="auth-remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--accent)' }}
                />
                Remember Me
              </label>
              <button
                className="btn-ghost btn-sm"
                onClick={() => switchMode('forgot')}
                style={{ fontSize: 12, color: 'var(--text3)', padding: '6px 10px' }}
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Confirm Password (signup only) */}
          {mode === 'signup' && (
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="auth-confirm">Confirm Password</label>
              <input
                id="auth-confirm"
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                placeholder="••••••••"
                onKeyDown={handleKey}
                autoComplete="new-password"
                style={confirm && confirm !== password ? { borderColor: 'var(--danger)' } : {}}
              />
              {confirm && confirm !== password && (
                <p style={{ fontSize: 11, color: 'var(--danger)', marginTop: 6 }}>
                  Passwords do not match.
                </p>
              )}
            </div>
          )}

          {/* Terms consent (signup only) */}
          {mode === 'signup' && (
            <div style={{ marginBottom: 20 }}>
              <label
                htmlFor="auth-terms"
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', userSelect: 'none',
                  padding: '10px 12px', borderRadius: 'var(--radius)',
                  border: `1px solid ${error && error.includes('Terms') ? 'var(--danger)' : 'var(--border)'}`,
                  background: error && error.includes('Terms') ? 'rgba(224,92,92,0.05)' : 'transparent',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <input
                  id="auth-terms"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={e => { setAgreedToTerms(e.target.checked); if (error && error.includes('Terms')) setError(''); }}
                  style={{ marginTop: 2, width: 15, height: 15, flexShrink: 0, accentColor: 'var(--accent)', cursor: 'pointer' }}
                />
                <span style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
                    Privacy Policy
                  </a>
                </span>
              </label>
              {error && error.includes('Terms') && (
                <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6, marginLeft: 2 }}>
                  You must agree to continue.
                </p>
              )}
            </div>
          )}

          {/* Error */}
          {error && !error.includes('Terms') && (
            <div style={{ marginBottom: 16 }}>
              <p role="alert" aria-live="assertive" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: error.includes('verify your email') ? 10 : 0 }}>
                {error}
              </p>
              {/* Unverified account — offer resend + guest browsing */}
              {error.includes('verify your email') && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button
                    className="btn-ghost btn-sm"
                    onClick={handleResendInline}
                    disabled={resending}
                    style={{ fontSize: 12, color: 'var(--accent)', padding: '5px 10px', textAlign: 'left' }}
                  >
                    {resending ? 'Sending…' : 'Resend verification email'}
                  </button>
                  {onGuestEntry && (
                    <button
                      className="btn-ghost btn-sm"
                      onClick={() => onGuestEntry(email.trim())}
                      style={{ fontSize: 12, color: 'var(--text3)', padding: '5px 10px', textAlign: 'left' }}
                    >
                      Browse as guest while you wait →
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Submit */}
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading || (mode === 'signup' && password.length > 0 && !pwAllMet)}
            style={{ width: '100%', opacity: (loading || (mode === 'signup' && password.length > 0 && !pwAllMet)) ? 0.7 : 1 }}
          >
            {loading ? 'Please wait…' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </div>

        {onGuestEntry && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button
              className="btn-ghost btn-sm"
              onClick={() => onGuestEntry()}
              style={{ color: 'var(--text3)', fontSize: 13 }}
            >
              Continue as Guest
            </button>
          </div>
        )}

        <p style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 11, marginTop: 16 }}>
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text3)', textDecoration: 'underline' }}>Terms of Service</a>
          {' · '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text3)', textDecoration: 'underline' }}>Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default AuthScreen;
